import { useEffect, useId, useState } from 'react'
import { Dialog } from '@/common/components/dialog'
import { Button } from '@/common/components/button'
import { useReopenTimesheet } from '@/common/hooks/useApprovals'
import * as styles from './ReopenTimesheetDialog.css'

export interface ReopenTimesheetDialogProps {
  /** The timesheet to reopen. Null keeps the dialog closed. */
  timesheetId: number | null
  /** Employee name shown in the confirmation copy. */
  employeeName?: string
  /** Pay period label shown in the confirmation copy, e.g. "Aug 1 – Aug 15". */
  payPeriodLabel?: string
  onClose: () => void
  onReopened?: () => void
}

/**
 * Confirmation dialog for the admin-only "reopen for correction" action.
 * Requires a reason and spells out the payroll consequences before sending
 * the timesheet back to the employee.
 */
export function ReopenTimesheetDialog({
  timesheetId,
  employeeName,
  payPeriodLabel,
  onClose,
  onReopened,
}: ReopenTimesheetDialogProps) {
  const [reason, setReason] = useState('')
  const reasonFieldId = useId()
  const reopen = useReopenTimesheet()

  // Start each confirmation from a clean slate rather than the previous row's reason.
  useEffect(() => {
    if (timesheetId != null) {
      setReason('')
      reopen.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timesheetId])

  const trimmedReason = reason.trim()

  const handleConfirm = () => {
    if (timesheetId == null || !trimmedReason) return
    reopen.mutate(
      { timesheetId, reason: trimmedReason },
      {
        onSuccess: () => {
          onReopened?.()
          onClose()
        },
      },
    )
  }

  const subject = [employeeName, payPeriodLabel].filter(Boolean).join(' — ')

  return (
    <Dialog open={timesheetId != null} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Content width={560}>
        <Dialog.Header>
          <Dialog.Title>Reopen timesheet for correction</Dialog.Title>
          <Dialog.Close />
        </Dialog.Header>
        <Dialog.Description>
          {subject ? `${subject}. ` : ''}
          This sends the timesheet back to the employee as &ldquo;needs changes&rdquo;. They correct
          it themselves and resubmit, and their manager approves it again.
        </Dialog.Description>

        <div className={styles.warning}>
          <strong>Before you reopen:</strong>
          <ul className={styles.warningList}>
            <li>
              The timesheet leaves the payroll export until it is approved again. If payroll for
              this period has already run, reopening corrects the record but does not reverse a
              payment already made.
            </li>
            <li>The employee re-enters the overdue reminder population for this pay period.</li>
            <li>The existing approval is cleared and has to be given again.</li>
          </ul>
        </div>

        <label className={styles.label} htmlFor={reasonFieldId}>
          Reason
        </label>
        <textarea
          id={reasonFieldId}
          className={styles.textarea}
          rows={3}
          maxLength={400}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="What needs correcting? The employee sees this."
        />

        {reopen.isError && (
          <div className={styles.errorText}>Failed to reopen: {(reopen.error as Error).message}</div>
        )}

        <Dialog.Footer>
          <Button variant="ghost" onClick={onClose} disabled={reopen.isPending}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirm} disabled={!trimmedReason || reopen.isPending}>
            {reopen.isPending ? 'Reopening…' : 'Reopen for correction'}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}
